import { Injectable, computed, inject } from '@angular/core';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Observable, throwError, from, of, timer } from 'rxjs';
import { catchError, switchMap, tap, map } from 'rxjs/operators';
import { API_CONFIG } from '../config/api.config';
import { AuthService } from '../components/default/login/service/login-service/auth.service';
import { User } from '../models/user.model';
import { AuthUrls, ApiUrlBuilder } from '../components/enums/api-urls/api-urls.enum';
import { EncryptionService } from './encryption.service';
import { StringHelper } from '../shared/utils/string.helper';
import { ChangePasswordDialogComponent } from '../components/admin/change-password-dialog/change-password-dialog.component';
import { MatDialog } from '@angular/material/dialog';

/**
 * Admin Login Request Interface
 */
export interface AdminLoginRequest {
  loginId: string;
  password?: string;
  isadmin?: boolean;
}

/**
 * Admin User Data from API
 */
export interface AdminUserData {
  name: string;
  companyName: string;
  email: string;
  mobileNo: string;
  userRole: string;
  permissions: string[];
  companySlug: string;
  hospitalId: number;
  isFirstTimeLogin?: boolean;
  passwordResetRequired?: boolean;
}

/**
 * Admin Login Data Response
 */
export interface AdminLoginData {
  success: boolean;
  message: string;
  user: AdminUserData;
  token: string;
  requiresPasswordReset?: boolean;
  isFirstLogin?: boolean;
}

/**
 * Admin Login Response Interface (matches actual API response)
 */
export interface AdminLoginResponse {
  success: boolean;
  data: AdminLoginData;
  error: any;
  traceId: string;
}

/**
 * Admin Authentication Service
 * Handles admin-specific authentication and API calls
 */
@Injectable({
  providedIn: 'root',
})
export class AdminAuthService {
  private http = inject(HttpClient);
  private authService = inject(AuthService);
  private encryptionService = inject(EncryptionService);
  private baseUrl = API_CONFIG.getBaseUrl();
  private dialog = inject(MatDialog);

  /**
   * Login admin user with username and password
   * @param username - Admin username (email)
   * @param password - Admin password
   * @returns Observable that emits the login response
   */
  login(username: string, password: string): Observable<AdminLoginResponse> {
    const loginUrl = ApiUrlBuilder.buildFullUrl(AuthUrls.LOGIN, this.baseUrl);
    const payload: AdminLoginRequest = {
      loginId: username.trim().toLowerCase(),
      password: password,
      isadmin: true,
    };

    return this.http.post<AdminLoginResponse>(loginUrl, payload).pipe(
      switchMap((response) => {
        // Handle successful login (async operations)
        return from(this.handleLoginSuccess(response, payload.loginId)).pipe(
          switchMap(() => {
         
            // Fetch and store assets status data after successful login
            // Chain it properly to ensure it executes
            return this.fetchAndStoreAssetsStatus().pipe(
              switchMap(() => {
                // Return the response after async operations complete
                return of(response);
              }),
            );
          }),
        );
      }),
      catchError((error: HttpErrorResponse) => {
        // Handle login error
        return this.handleLoginError(error);
      }),
    );
  }

  /**
   * Handle successful login response
   * @param response - Login response from API
   * @param username - Username used for login
   */
  private async handleLoginSuccess(
    response: AdminLoginResponse,
    username: string,
  ): Promise<void> {
    try {
      // Extract data from nested response structure
      if (!response.success || !response.data) {
        throw new Error('Invalid response structure');
      }

      const loginData = response.data;
      const token = loginData.token;

      // Store token - this will be used by auth interceptor for subsequent API calls
      if (token) {
        await this.authService.setToken(token);
      }

      // Map response to User model
      const user: User = this.mapResponseToUser(loginData.user, username);

      // Update auth service with user info
      this.authService.setUser(user);
    } catch (error) {
      throw error;
    }
  }

  /**
   * Map API user data to User model
   * @param userData - User data from API
   * @param username - Username used for login (fallback)
   * @returns User object
   */
  private mapResponseToUser(userData: AdminUserData, username: string): User {
    // Parse name into firstName and lastName
    const nameParts = userData.name?.split(' ') || [];
    const firstName = nameParts[0] || 'Admin';
    const lastName = nameParts.slice(1).join(' ') || 'User';

    // Map userRole to User model role type
    const roleMapping: { [key: string]: User['role'] } = {
      ADMIN: 'Admin',
      DOCTOR: 'Doctor',
      NURSE: 'Nurse',
      RECEPTIONIST: 'Receptionist',
      PHARMACIST: 'Pharmacist',
    };
    const role = roleMapping[userData.userRole?.toUpperCase()] || 'Admin';

    return {
      id: userData.email || username,
      firstName: firstName,
      lastName: lastName,
      email: userData.email || username,
      phone: userData.mobileNo || '',
      role: role,
      isActive: true,
      createdAt: new Date(),
    };
  }

  /**
   * Handle login error
   * @param error - HTTP error response
   * @returns Observable that emits an error
   */
  private handleLoginError(error: HttpErrorResponse): Observable<never> {
    let errorMessage = 'Login failed. Please try again.';

    if (error.error instanceof ErrorEvent) {
      // Client-side error
      errorMessage = `Network error: ${error.error.message}`;
    } else {
      // Server-side error
      switch (error.status) {
        case 401:
          errorMessage = 'Invalid username or password';
          break;
        case 403:
          errorMessage = 'Access denied. Please contact administrator.';
          break;
        case 404:
          errorMessage = 'Login endpoint not found';
          break;
        case 500:
          errorMessage = 'Server error. Please try again later.';
          break;
        default:
          errorMessage =
            error.error?.message ||
            error.message ||
            'An unexpected error occurred';
      }
    }

    console.error('Admin login error:', error);
    return throwError(() => new Error(errorMessage));
  }

  /**
   * Initialize assets status data - call on app load/page reload
   * Checks if user is authenticated and fetches assets status data
   * @returns Observable that completes when data is stored or if not authenticated
   */
  initializeAssetsStatus(): Observable<void> {
    // Check if user is authenticated
    if (!this.authService.isAuthenticated()) {
      return of(undefined as void);
    }

    // Check if user is admin
    if (!this.authService.hasRole('Admin')) {
      return of(undefined as void);
    }
    return this.fetchAndStoreAssetsStatus();
  }

  /**
   * Fetch All Assets Status API and store encrypted data in sessionStorage
   * Called once after successful login and on page reload
   * Note: Auth interceptor will automatically add the Authorization header
   * @returns Observable that completes when data is stored
   */
  private fetchAndStoreAssetsStatus(): Observable<void> {
    const assetsStatusUrl = ApiUrlBuilder.buildFullUrl(
      AuthUrls.All_ASSETS_STATUS_API,
      this.baseUrl,
    );


    // Wait for token to be available (with retry mechanism)
    return from(this.authService.getToken()).pipe(
      switchMap((token) => {
        if (!token) {
          console.warn('⚠️ Token not available yet, waiting...');
          // Wait 100ms and retry
          return timer(100).pipe(
            switchMap(() => from(this.authService.getToken())),
            switchMap((retryToken) => {
              if (!retryToken) {
                console.error('❌ Token still not available after retry');
                return of(undefined as void);
              }
              return this.makeAssetsStatusRequest(assetsStatusUrl);
            }),
          );
        }
        return this.makeAssetsStatusRequest(assetsStatusUrl);
      }),
      catchError((error) => {
        console.error('❌ Error in fetchAndStoreAssetsStatus:', error);
        return of(undefined as void);
      }),
    );
  }

  /**
   * Make the actual API request for assets status
   * @param assetsStatusUrl - The full URL for the assets status API
   * @returns Observable that completes when data is stored
   */
  private makeAssetsStatusRequest(assetsStatusUrl: string): Observable<void> {

    // Make the API call (auth interceptor will add the token automatically)
    return this.http.get<any>(assetsStatusUrl).pipe(
      switchMap((response) => {
    

        // Store the full response data (or data.values if that's the structure)
        // The API might return: { data: { values: [...] } } or { data: [...] }
        const dataToStore =
          response?.data?.values || response?.data || response;

        // Encrypt the response data
        return from(this.encryptionService.encryptObject(dataToStore)).pipe(
          tap((encryptedData) => {
            // Store encrypted data in sessionStorage
            sessionStorage.setItem('assetsStatusData', encryptedData);
         
          }),
          switchMap(() => of(undefined as void)),
          catchError((encryptError) => {
            console.error(
              '❌ Failed to encrypt assets status data:',
              encryptError,
            );
            // Store unencrypted as fallback (not recommended for production)
            try {
              sessionStorage.setItem(
                'assetsStatusData',
                JSON.stringify(response),
              );
              console.log(
                '⚠️ Assets status data stored unencrypted (fallback)',
              );
            } catch (storageError) {
              console.error(
                '❌ Failed to store assets status data:',
                storageError,
              );
            }
            return of(undefined as void);
          }),
        );
      }),
      catchError((error) => {
        console.error('❌ Failed to fetch assets status:', error);
        console.error('Error details:', {
          status: error.status,
          message: error.message,
          url: assetsStatusUrl,
        });
        // Return void to continue the chain even if API fails
        return of(undefined as void);
      }),
    );
  }

  /**
   * Get decrypted assets status data from sessionStorage
   * @returns Observable that emits the decrypted assets status data or null
   */
  getAssetsStatusData(): Observable<any | null> {
    const encryptedData = sessionStorage.getItem('assetsStatusData');

    if (!encryptedData) {
      return of(null);
    }

    return from(this.encryptionService.decryptObject<any>(encryptedData)).pipe(
      catchError((error) => {
        console.error('Failed to decrypt assets status data:', error);
        // Try to parse as unencrypted JSON (fallback)
        try {
          return of(JSON.parse(encryptedData));
        } catch (parseError) {
          console.error('Failed to parse assets status data:', parseError);
          return of(null);
        }
      }),
    );
  }

  /**
   * Get hospital type options from assets status data
   * Uses StringHelper to filter by name "hospitalType" and transforms to {id, labelName} format
   * @returns Observable that emits array of hospital type options or empty array
   */
  getHospitalTypeOptions(): Observable<
    Array<{ id: number; labelName: string }>
  > {
    // Use StringHelper to get options by type
    return StringHelper.getOptionsByType(this, 'hospitalType').pipe(
      // Transform from {id, name} to {id, labelName} format for backward compatibility
      map((options) =>
        options.map((option) => ({
          id: option.id,
          labelName: option.name,
        })),
      ),
      catchError((error) => {
        console.error('❌ Error getting hospital type options:', error);
        return of([]);
      }),
    );
  }

  /**
   * Clear assets status data from sessionStorage
   */
  clearAssetsStatusData(): void {
    sessionStorage.removeItem('assetsStatusData');
  }

  /**
   * Logout admin user
   * Calls the logout API endpoint and then clears local storage
   * @returns Observable that completes when logout is done
   */
  logout(): Observable<void> {
    const logoutUrl = ApiUrlBuilder.buildFullUrl(AuthUrls.LOGOUT, this.baseUrl);
    const payload: AdminLoginRequest = {
      loginId: computed(() => this.authService.user())()?.email || '',
      isadmin: true,
    };
    // Call logout API endpoint
    return this.http.post<void>(logoutUrl, payload).pipe(
      // Even if API call succeeds, clear local storage
      switchMap(() => {
        // Clear assets status data from sessionStorage
        this.clearAssetsStatusData();
        // Clear local auth state (user, tokens, etc.)
        // Note: authService.logout() navigates to '/login', but the component
        // will immediately navigate to '/admin/login', overriding it
        this.authService.logout();
        return of(undefined);
      }),
      // If API call fails, still logout locally (e.g., network error, token already invalid)
      catchError((error: HttpErrorResponse) => {
        console.warn(
          'Logout API call failed, but clearing local session:',
          error,
        );
        // this.authService.logout();
        return throwError(() => error);
      }),
    );
  }
}

